import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  authAPI,
  isTransientServerError,
} from "../utils/api";
import { resetClientCaches } from "../utils/clientCache";

const AuthContext = createContext();

let sessionRequest = null;
let bootstrapRequest = null;

function normalizeSession(data) {
  if (!data || typeof data !== "object") return null;
  if (data.user) return data;
  if (data.status || data.role || data.ROWID) {
    return { user: data, permissions: null, profile: null };
  }
  return null;
}

function profileFromUser(user) {
  const name = String(user?.name || "").trim();
  const [firstName, ...rest] = name.split(/\s+/);
  return {
    firstName: firstName || "",
    lastName: rest.join(" ") || "",
    mailid: user?.email || "",
    timeZone: "",
    createdTime: "",
  };
}

function profileFromCatalystAuth(content) {
  return {
    firstName: content?.first_name || "",
    lastName: content?.last_name || "",
    mailid: content?.email_id || "",
    timeZone: content?.time_zone || "",
    createdTime: content?.created_time || "",
  };
}

function requestSession({ force = false } = {}) {
  if (force || !sessionRequest) {
    sessionRequest = authAPI.getMe().finally(() => {
      sessionRequest = null;
    });
  }
  return sessionRequest;
}

function isActiveUser(user) {
  return String(user?.status || "").toUpperCase() === "ACTIVE";
}

export const AuthProvider = ({ children }) => {
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [dbUser, setDbUser] = useState(null);
  const [sessionPermissions, setSessionPermissions] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [isEvalrisOperator, setIsEvalrisOperator] = useState(false);
  const [quickActions, setQuickActions] = useState([]);
  const [sessionError, setSessionError] = useState(null);
  const mountedRef = useRef(true);
  const previousOrgScopeRef = useRef(null);

  const applySession = useCallback((session) => {
    setDbUser(session?.user ?? null);
    setSessionPermissions(session?.permissions ?? null);
    setOrganization(session?.organization ?? null);
    setIsEvalrisOperator(Boolean(session?.isEvalrisOperator));
    setQuickActions(Array.isArray(session?.quick_actions) ? session.quick_actions : []);
    setUserDetails(session?.profile ?? profileFromUser(session?.user));
    setSessionError(null);
  }, []);

  const clearSession = useCallback(() => {
    resetClientCaches();
    setDbUser(null);
    setSessionPermissions(null);
    setOrganization(null);
    setIsEvalrisOperator(false);
    setQuickActions([]);
    setUserDetails(null);
    setIsUserAuthenticated(false);
    previousOrgScopeRef.current = null;
  }, []);

  const applyPendingSession = useCallback(
    (user, profile) => {
      applySession({
        user,
        profile,
        organization: null,
        permissions: null,
        isEvalrisOperator: false,
      });
    },
    [applySession]
  );

  const refreshSession = useCallback(async () => {
    const meRes = await requestSession({ force: true });
    const session = normalizeSession(meRes.data?.data);
    if (!session?.user) {
      throw new Error("Invalid session response");
    }
    applySession(session);
    setIsUserAuthenticated(true);
    return session;
  }, [applySession]);

  const bootstrapSession = useCallback(async () => {
    if (bootstrapRequest) {
      return bootstrapRequest;
    }

    bootstrapRequest = (async () => {
    if (!window.catalyst?.auth) {
      clearSession();
      return;
    }

    let authResult;
    try {
      authResult = await window.catalyst.auth.isUserAuthenticated();
    } catch (err) {
      clearSession();
      return;
    }

    if (!authResult?.content) {
      clearSession();
      return;
    }

    const profile = profileFromCatalystAuth(authResult.content);
    setUserDetails(profile);
    setIsUserAuthenticated(true);

    try {
      const syncRes = await authAPI.syncUser();
      const user = syncRes.data?.data;
      if (!user || typeof user !== "object") {
        throw new Error("Invalid sync response");
      }

      if (!isActiveUser(user)) {
        applyPendingSession(user, profile);
        return;
      }

      const meRes = await authAPI.getMe();
      const session = normalizeSession(meRes.data?.data);
      if (!session?.user) {
        throw new Error("Invalid session response");
      }
      applySession(session);
    } catch (err) {
      if (isTransientServerError(err)) {
        setSessionError(
          new Error(
            "The server is temporarily unavailable. Wait a few seconds and refresh the page."
          )
        );
        console.warn("Session bootstrap failed (transient)", err);
        return;
      }
      clearSession();
      setSessionError(
        new Error(err.response?.data?.message || err.message || "Failed to load session")
      );
      console.error("Failed to bootstrap session", err.response?.data || err);
    }
    })().finally(() => {
      bootstrapRequest = null;
    });

    return bootstrapRequest;
  }, [applyPendingSession, applySession, clearSession]);

  useEffect(() => {
    const orgScopeKey =
      organization?.ROWID != null
        ? String(organization.ROWID)
        : dbUser?.org_id != null
          ? String(dbUser.org_id)
          : null;

    if (
      previousOrgScopeRef.current != null &&
      orgScopeKey != null &&
      previousOrgScopeRef.current !== orgScopeKey
    ) {
      resetClientCaches();
    }

    if (orgScopeKey != null) {
      previousOrgScopeRef.current = orgScopeKey;
    }
  }, [organization?.ROWID, dbUser?.org_id]);

  useEffect(() => {
    mountedRef.current = true;

    (async () => {
      setIsFetching(true);
      setSessionError(null);
      await bootstrapSession();
      if (mountedRef.current) {
        setIsFetching(false);
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, [bootstrapSession]);

  useEffect(() => {
    const onSuspended = () => {
      refreshSession().catch(() => {});
    };
    window.addEventListener("evalris:account-suspended", onSuspended);
    return () => window.removeEventListener("evalris:account-suspended", onSuspended);
  }, [refreshSession]);

  const contextValue = useMemo(
    () => ({
      isUserAuthenticated,
      setIsUserAuthenticated,
      userDetails,
      setUserDetails,
      dbUser,
      setDbUser,
      sessionPermissions,
      setSessionPermissions,
      organization,
      isEvalrisOperator,
      quickActions,
      setQuickActions,
      refreshSession,
      sessionError,
      isFetching,
    }),
    [
      isUserAuthenticated,
      userDetails,
      dbUser,
      sessionPermissions,
      organization,
      isEvalrisOperator,
      quickActions,
      refreshSession,
      sessionError,
      isFetching,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
